import { MultipageNavigationBar } from 'front-end/src/pages/topic-detail-page/components/MultipageNavigationBar'
import React, { createRef, FC, useContext, useEffect, useState } from 'react'
import { Trans } from '@lingui/macro'
import classNames from 'classnames'
import _ from 'lodash'
import { Link } from 'react-router-dom'

import { TopicCompletionPage } from './TopicCompletionPage'
import TopicVersionTitleAndSummary from './TopicVersionTitleAndSummary'
import { IElement } from './types/IElement'
import { TopbarView } from './components/topbar/TopbarView'
import { ScormPage } from '../scorm-page/ScormPage'
import { ScormPagePreview } from '../scorm-page-preview/ScormPagePreview'
import { TopicPageMode } from './TopicDetailPage'
import Message, { MessageTypes } from '../../components/common/message/Message'
import { staticLinks } from 'shared/linkBuilder'
import { QuizElementTypes } from 'types/TopicElementTypeEnum'
import HeadTitle from '../../components/common/head/HeadTitle'
import { VideoAssessmentPage } from '../video-assessment-page/VideoAssessmentPage'
import { VideoAssessmentGradebookPage } from '../video-assessment-gradebook-page/VideoAssessmentGradebookPage'
import { SinglePageElement } from './components/SinglePageElement'
import { MultiPageElement } from './components/MultipageElement'

import './TopicDetailPage.less'
import TopbarEdit from './components/topbar/TopbarEdit'
import {
  TopicElementType,
  TopicVersionType
} from '../../graphql/private/PrivateSchemaTypes'
import { TopicDetailContext } from './TopicDetailContext'

export const videoAssessmentTopicVersionTypesForFE = [
  TopicVersionType.VIDEO_ASSESSMENT_GROUP,
  TopicVersionType.VIDEO_ASSESSMENT_INDIVIDUAL,
  TopicVersionType.VIDEO_ASSESSMENT_INTERACTIVE,
  TopicVersionType.VIDEO_ASSESSMENT_Q_AND_A
]

let visitedElementIds: number[] = []

export const StudentTopicDetail: FC = () => {
  const { topic, updateTopicProgress, topicVersion, mode } =
    useContext(TopicDetailContext)
  const [currentElementIndex, setCurrentElementIndex] = useState(-1)
  const [previousElementIndex, setPreviousElementIndex] = useState(-1)
  const [currentElement, setCurrentElementState] = useState<IElement | null>()

  const setCurrentElement = async (newElementIndex: number) => {
    if (topicVersion?.topic_type === TopicVersionType.m) {
      setPreviousElementIndex(currentElementIndex)
      setCurrentElementIndex(newElementIndex)
    }
    if (topicVersion && topicVersion[elementsPath]?.length) {
      const element = topicVersion[elementsPath][newElementIndex]
      if (topicVersion?.topic_type === TopicVersionType.m) {
        setCurrentElementState(element)
      }
      if (element) {
        if (!visitedElementIds.includes(element.id)) {
          visitedElementIds.push(element.id)
          await updateTopicProgress(element.id)
        }
      }
    }
  }

  const setCurrentElementDebounced = _.debounce(setCurrentElement, 300)

  const panelElmRef = createRef<HTMLDivElement>()

  const elementsPath =
    mode === TopicPageMode.Preview ? 'elements' : 'publishedElements'

  useEffect(() => {
    visitedElementIds = []
    panelElmRef.current && panelElmRef.current.scrollTo(0, 0)
  }, [topic.id])

  useEffect(() => {
    openLastViewedElement()
  }, [])

  const openLastViewedElement = async () => {
    // Let's open last viewed element in Multipage topic, only if there is any stored last viewed element in the enrolment
    if (mode === TopicPageMode.Preview) {
      return
    }

    const maxVisitedElement = topic.enrolmentOrSelfEnrol?.max_visited_element
    if (
      topicVersion?.topic_type === TopicVersionType.m &&
      topicVersion[elementsPath]
    ) {
      if (topic?.enrolmentOrSelfEnrol?.completion_date) {
        // If enrolment is completed - show first slide
        await setCurrentElement(-1)
      } else if (maxVisitedElement) {
        // @ts-ignore
        const currentElementIndex = topicVersion[elementsPath].findIndex(
          (e) => e.id === maxVisitedElement
        )
        await setCurrentElement(currentElementIndex)
      }
    }
  }

  const renderElementsOrCurrentElement = () => {
    if (!topic || !topicVersion) {
      return null
    }

    const elements = topicVersion[elementsPath]

    if (topicVersion.topic_type === TopicVersionType.scorm) {
      if (mode === TopicPageMode.Preview) {
        return <ScormPagePreview topic={topic} />
      } else {
        if (!topic?.enrolmentOrSelfEnrol?.id) {
          return null
        }
        return (
          <ScormPage
            shareId={topic.enrolmentOrSelfEnrol.id}
            isPathway={mode === TopicPageMode.PathwayView}
          />
        )
      }
    }
    if (
      topicVersion?.topic_type &&
      videoAssessmentTopicVersionTypesForFE.includes(topicVersion.topic_type)
    ) {
      if (mode === TopicPageMode.Gradebook) {
        return <VideoAssessmentGradebookPage topic={topic} />
      }
      if (topic?.enrolmentOrSelfEnrol?.id) {
        return (
          <VideoAssessmentPage
            shareId={topic?.enrolmentOrSelfEnrol?.id}
            isPathway={mode === TopicPageMode.PathwayView}
          />
        )
      }
    } else if (elements) {
      if (topicVersion.topic_type === TopicVersionType.m) {
        return (
          <MultiPageElement
            // @ts-ignore TODO
            elements={elements}
            previousElementIndex={previousElementIndex}
            currentElementIndex={currentElementIndex}
          />
        )
      } else {
        return elements.map((element, index) => {
          return (
            <SinglePageElement
              key={element.id}
              element={element}
              index={index}
              onIsVisible={(index) => {
                setCurrentElementDebounced(index)
              }}
            />
          )
        })
      }
    }
  }

  const progress = () => {
    if (!topicVersion || !topicVersion[elementsPath]?.length) {
      return 0
    }

    // @ts-ignore
    const singleElemProgressSize = 100 / (topicVersion[elementsPath].length + 1)
    return singleElemProgressSize * (currentElementIndex + 1)
  }

  // TODO cover with a unit test once we figure out how to load up VisibilitySensor in jest
  const canGoToNextElement = () => {
    if (!topic || (topicVersion && !topicVersion[elementsPath]?.length)) {
      return false
    }

    if (
      topic.enrolmentOrSelfEnrol &&
      currentElement &&
      QuizElementTypes.includes(currentElement.type as any)
    ) {
      if (
        currentElement.type === TopicElementType.QUIZ_FREE &&
        !currentElement.data?.minLength
      ) {
        return true
      } else {
        const currentAnswer = topic?.enrolmentOrSelfEnrol?.quizResponses?.find(
          (response) => {
            return response.topic_element_id === currentElement.id
          }
        )
        return !!currentAnswer
      }
    }
    // @ts-expect-error
    if (currentElementIndex <= topicVersion[elementsPath].length - 1) {
      return true
    }
  }

  // User might not have enough access rights to view this combination of topic and view mode.
  // Depends on multiple things like user role, view mode, assigned workspaces to user.
  // For example STUDENT might not have access rights to open edit form of some topic.
  if (!topicVersion || !topic) {
    return (
      <div className="page--error">
        <Message type={MessageTypes.WARNING}>
          <div>
            <Trans>
              Unable to load resource at the moment. You might not have
              sufficient access privileges.
            </Trans>
          </div>
          <div>
            <Link to={staticLinks.home}>
              <Trans>Return home</Trans>
            </Link>
          </div>
        </Message>
      </div>
    )
  }

  const isSinglePage = topicVersion.topic_type === TopicVersionType.s
  const isMultiPage = topicVersion.topic_type === TopicVersionType.m
  const isScorm = topicVersion.topic_type === TopicVersionType.scorm

  return (
    <div
      className={classNames('panel topic-mode-learn', {
        'topic-view-single': isSinglePage || isScorm,
        'topic-view-multi': isMultiPage
      })}
    >
      <HeadTitle title={topicVersion.title} />
      {mode === TopicPageMode.Preview ? <TopbarEdit /> : <TopbarView />}

      {isMultiPage && (
        <div className="topic-progress">
          <div
            className="topic-progress-current"
            style={{ width: `${progress()}%` }}
          />
        </div>
      )}
      <div className="panel-content" ref={panelElmRef}>
        <div
          className={classNames('pages-wrapper', {
            'full-wrapper': !isSinglePage || !isMultiPage
          })}
        >
          {isSinglePage && (
            <div className="page page-system-generated-title">
              <TopicVersionTitleAndSummary
                title={topicVersion.title}
                summary={topicVersion.summary}
                isPreview={mode === TopicPageMode.Preview}
              />
            </div>
          )}
          {renderElementsOrCurrentElement()}
          {isSinglePage && <TopicCompletionPage />}
        </div>
      </div>
      {isMultiPage && (
        <MultipageNavigationBar
          canGoToNextElement={canGoToNextElement}
          currentElementIndex={currentElementIndex}
          setCurrentElement={setCurrentElement}
        />
      )}
    </div>
  )
}
